import React, { useState } from 'react';
import './App.css';
import Form from './Form';
import Spot from './Spot';
import Od from './Od';
import VcpuMem from './VcpuMem';
import Scores from './Scores';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import { Helmet } from 'react-helmet';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Grid, Box, Container } from '@mui/material';
import EC2MarketPlace from './EC2MarketPlace';
import CheapestVcpuSpot from './CheapestVcpuSpot';
import NavBar from './NavBar';
import { Route, Routes } from 'react-router';
import { useLocation } from 'react-router-dom';
import SpotAnalyzer from './CheapestSpotInstanceFinder';
import PricingTable from './components/PricingTable';
import cyberpunkTheme, { cyberpunkColors } from './theme/cyberpunkTheme';
import { API_CONFIG, ERROR_MESSAGES } from './constants';

const { BASE_URL } = API_CONFIG;
const TITLE = 'EC2 Pricing Hub';
const MAIN_ROUTES = ['/EC2MarketPlace', '/CheapestVcpuSpot', '/SpotAnalyzer'];

function App() {
  // Pricing data state
  const [onDemand, setOnDemand] = useState({});
  const [spot, setSpot] = useState({});
  const [reservedInstance, setReservedInstance] = useState({});
  const [savingsPlan, setSavingsPlan] = useState({});
  const [marketplace, setMarketplace] = useState({});
  const [instances, setInstances] = useState({ ec2_type: 'm5.large' });
  const [vcpu, setVcpu] = useState({});
  const [scores, setScores] = useState({});

  // Loading states
  const [odLoading, setOdLoading] = useState(false);
  const [spotLoading, setSpotLoading] = useState(false);
  const [marketLoading, setMarketLoading] = useState(false);
  const [spLoading, setSpLoading] = useState(false);

  // UI state
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const location = useLocation();
  const isMainRoute = !MAIN_ROUTES.includes(location.pathname);

  // Check if we have any pricing data to display
  const hasPricingData = onDemand.od_rate || spot.spot_rate || vcpu.array || scores.scores;

  // Fetch functions
  async function fetchOnDemand(e) {
    try {
      setError(null);
      const { region, ec2_type, os_type } = getFormValues(e);
      setOdLoading(true);
      e.preventDefault();

      const response = await fetch(`${BASE_URL}/od/${region}/${ec2_type}/${os_type}/`);
      if (!response.ok) throw new Error('Server error');

      const apiData = await response.json();
      if (region && ec2_type && os_type) {
        setOnDemand({ od_rate: apiData.rate });
      }
    } catch (err) {
      setError(ERROR_MESSAGES.GENERIC);
    } finally {
      setOdLoading(false);
    }
  }

  async function fetchSpot(e) {
    try {
      setError(null);
      const { region, ec2_type, os_type } = getFormValues(e);
      setSpotLoading(true);
      e.preventDefault();

      const response = await fetch(`${BASE_URL}/spot/${region}/${ec2_type}/${os_type}/`);
      if (!response.ok) throw new Error('Server error');

      const apiData = await response.json();
      if (region) {
        setSpot({ spot_rate: apiData.rate });
      }
    } catch (err) {
      setError(ERROR_MESSAGES.GENERIC);
    } finally {
      setSpotLoading(false);
    }
  }

  async function fetchRi(e) {
    try {
      setError(null);
      const { region, ec2_type, os_type } = getFormValues(e);
      e.preventDefault();

      const response = await fetch(`${BASE_URL}/ri/${region}/${ec2_type}/${os_type}/`);
      if (!response.ok) throw new Error('Server error');

      const apiData = await response.json();
      if (region) {
        setReservedInstance({ ri: apiData });
      }
    } catch (err) {
      setError(ERROR_MESSAGES.GENERIC);
    }
  }

  async function fetchSp(e) {
    try {
      setError(null);
      const { region, ec2_type, os_type } = getFormValues(e);
      setSpLoading(true);
      e.preventDefault();

      const response = await fetch(`${BASE_URL}/sp/${region}/${ec2_type}/${os_type}/`);
      if (!response.ok) throw new Error('Server error');

      const apiData = await response.json();
      if (region) {
        setSavingsPlan({ sp: apiData });
      }
    } catch (err) {
      setError(ERROR_MESSAGES.GENERIC);
    } finally {
      setSpLoading(false);
    }
  }

  async function fetchMarket(e) {
    try {
      setError(null);
      const { region, ec2_type, os_type } = getFormValues(e);
      setMarketLoading(true);
      e.preventDefault();

      const response = await fetch(`${BASE_URL}/market/${region}/${ec2_type}/${os_type}/`);
      if (!response.ok) throw new Error('Server error');

      const apiData = await response.json();
      if (region) {
        setMarketplace({ market: apiData });
      }
    } catch (err) {
      setError(ERROR_MESSAGES.GENERIC);
    } finally {
      setMarketLoading(false);
    }
  }

  async function fetchInstances(e) {
    try {
      setError(null);
      const region = e.target.elements.region.value;
      e.preventDefault();

      if (!region) {
        setError('Please select a region.');
        return;
      }

      const response = await fetch(`${BASE_URL}/instances/${region}/`);
      if (!response.ok) throw new Error('Server error');

      const apiData = await response.json();
      setInstances({ array: apiData });
    } catch (err) {
      setError(ERROR_MESSAGES.GENERIC);
    }
  }

  async function fetchVcpu(e) {
    try {
      setError(null);
      const ec2_type = e.target.elements.ec2_type.value;
      e.preventDefault();

      if (!ec2_type) {
        setError('Please select an EC2 type.');
        return;
      }

      const response = await fetch(`${BASE_URL}/vcpu-mem/${ec2_type}/`);
      if (!response.ok) throw new Error('Server error');

      const apiData = await response.json();
      setVcpu({ array: apiData });
    } catch (err) {
      setError(ERROR_MESSAGES.GENERIC);
    }
  }

  async function fetchScores(e) {
    try {
      setError(null);
      const { region, ec2_type, os_type } = getFormValues(e);
      e.preventDefault();

      if (!region || !ec2_type || !os_type) {
        setError('Please ensure all fields are filled out.');
        return;
      }

      const response = await fetch(`${BASE_URL}/scores/${ec2_type}/${region}/${os_type}/`);
      if (!response.ok) throw new Error('Server error');

      const apiData = await response.json();
      setScores({ scores: apiData });
    } catch (err) {
      setError(ERROR_MESSAGES.GENERIC);
    }
  }

  // Helper to extract form values
  function getFormValues(e) {
    return {
      region: e.target.elements.region.value,
      ec2_type: e.target.elements.ec2_type.value,
      os_type: e.target.elements.os_type.value,
    };
  }

  // Fetch all data
  async function fetchData(e) {
    fetchInstances(e);
    fetchOnDemand(e);
    fetchSpot(e);
    fetchRi(e);
    fetchSp(e);
    fetchMarket(e);
    fetchVcpu(e);
    fetchScores(e);
  }

  async function fetchRiData(e) {
    fetchInstances(e);
    fetchOnDemand(e);
    fetchMarket(e);
  }

  const handleSubmit = () => {
    setHasSearched(true);
  };

  return (
    <div className="App">
      <ThemeProvider theme={cyberpunkTheme}>
        <CssBaseline />
        <NavBar />
        <div className="content">
          <Routes>
            <Route path="/EC2MarketPlace" element={<EC2MarketPlace getRi={fetchRiData} />} />
            <Route path="/CheapestVcpuSpot" element={<CheapestVcpuSpot />} />
            <Route path="/SpotAnalyzer" element={<SpotAnalyzer />} />
          </Routes>

          <Helmet>
            <title>{TITLE}</title>
            <meta
              name="description"
              content="EC2 Pricing Hub - Compare AWS EC2 instance pricing across On-Demand, Reserved Instances, Spot Instances, and Savings Plans."
            />
            <script async src="https://www.googletagmanager.com/gtag/js?id=G-0W0H3EM6F1"></script>
            <script>{`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments)}
              gtag('js', new Date());
              gtag('config', 'G-0W0H3EM6F1');
            `}</script>
          </Helmet>

          {error && (
            <Box className="error-message" sx={{ maxWidth: 600, mx: 'auto', mt: 2 }}>
              <Typography variant="h6">{error}</Typography>
            </Box>
          )}

          {isMainRoute && (
            <Container maxWidth="xl" sx={{ py: 3 }}>
              {/* Compact Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: '"Orbitron", sans-serif',
                    fontWeight: 700,
                    color: cyberpunkColors.neonCyan,
                    textShadow: `0 0 20px ${cyberpunkColors.neonCyan}80, 0 0 40px ${cyberpunkColors.neonCyan}40`,
                    mb: 1,
                    letterSpacing: '0.15em',
                  }}
                >
                  EC2 PRICING HUB
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: cyberpunkColors.textSecondary,
                    fontFamily: '"Rajdhani", sans-serif',
                    fontSize: '1.1rem',
                  }}
                >
                  Compare pricing across On-Demand, Spot, Reserved Instances & Savings Plans
                </Typography>
              </Box>

              {/* Main Content Grid - Form and Stats Side by Side */}
              <Grid container spacing={3}>
                {/* Left Column - Form */}
                <Grid item xs={12} md={hasPricingData ? 5 : 12} lg={hasPricingData ? 4 : 8} sx={{ mx: hasPricingData ? 0 : 'auto' }}>
                  <Box
                    sx={{
                      background: 'linear-gradient(145deg, #1a1a24, #0d0d14)',
                      border: `1px solid ${cyberpunkColors.neonCyan}40`,
                      borderRadius: 2,
                      p: 3,
                      position: 'sticky',
                      top: 80,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: '"Orbitron", sans-serif',
                        color: cyberpunkColors.neonCyan,
                        mb: 2,
                        fontSize: '0.9rem',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Select Instance
                    </Typography>
                    {odLoading && (
                      <LinearProgress
                        sx={{
                          mb: 2,
                          height: 2,
                          backgroundColor: 'rgba(0, 255, 255, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            background: `linear-gradient(90deg, ${cyberpunkColors.neonCyan}, ${cyberpunkColors.neonMagenta})`,
                          },
                        }}
                      />
                    )}
                    <Form getSpot={fetchData} instance={instances.array} onSubmit={handleSubmit} />
                  </Box>
                </Grid>

                {/* Right Column - Stats Dashboard */}
                {hasPricingData && (
                  <Grid item xs={12} md={7} lg={8}>
                    {/* Quick Stats Grid */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {onDemand.od_rate && (
                        <Grid item xs={6} lg={3}>
                          <Od od_rate={onDemand.od_rate} />
                        </Grid>
                      )}
                      {spot.spot_rate && (
                        <Grid item xs={6} lg={3}>
                          <Spot rate={spot.spot_rate} od_rate={onDemand.od_rate} />
                        </Grid>
                      )}
                      {vcpu.array && (
                        <Grid item xs={6} lg={3}>
                          <VcpuMem stats={vcpu} />
                        </Grid>
                      )}
                      {scores.scores && (
                        <Grid item xs={6} lg={3}>
                          <Scores stats={scores} />
                        </Grid>
                      )}
                    </Grid>

                    {/* Pricing Tables */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {spotLoading && (
                        <LinearProgress
                          sx={{
                            height: 2,
                            backgroundColor: 'rgba(0, 255, 255, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              background: `linear-gradient(90deg, ${cyberpunkColors.neonCyan}, ${cyberpunkColors.neonMagenta})`,
                            },
                          }}
                        />
                      )}

                      <PricingTable
                        type="ri"
                        data={reservedInstance.ri}
                        odRate={onDemand.od_rate}
                      />

                      {marketLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} sx={{ color: cyberpunkColors.neonCyan }} />
                        </Box>
                      )}

                      <PricingTable
                        type="market"
                        data={marketplace.market}
                        odRate={onDemand.od_rate}
                      />

                      {spLoading && (
                        <LinearProgress
                          sx={{
                            height: 2,
                            backgroundColor: 'rgba(0, 255, 255, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              background: `linear-gradient(90deg, ${cyberpunkColors.neonCyan}, ${cyberpunkColors.neonMagenta})`,
                            },
                          }}
                        />
                      )}

                      <PricingTable
                        type="sp"
                        data={savingsPlan.sp}
                        odRate={onDemand.od_rate}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Container>
          )}
        </div>

        <footer className="footer">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {/* Cool Tech Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  color: cyberpunkColors.neonMagenta,
                  fontSize: '0.7rem',
                  fontFamily: '"Orbitron", sans-serif',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  textShadow: `0 0 10px ${cyberpunkColors.neonMagenta}50`,
                }}
              >
                Cool Tech
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  p: 1.5,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <a
                  href="https://upwind.io"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <img
                    src="https://www.upwind.io/wp-content/uploads/2024/07/Logo-Components-1.svg"
                    alt="Upwind"
                    style={{
                      height: '26px',
                      filter: 'brightness(0) invert(1)',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseOver={(e) => e.target.style.filter = 'brightness(0) invert(1) drop-shadow(0 0 8px rgba(255, 255, 255, 0.8))'}
                    onMouseOut={(e) => e.target.style.filter = 'brightness(0) invert(1)'}
                  />
                </a>
                <a
                  href="https://scaleops.com"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <img
                    src="https://scaleops.com/wp-content/uploads/2024/09/ScaleOps_LOGO-1.svg"
                    alt="ScaleOps"
                    style={{
                      height: '24px',
                      filter: 'brightness(0) invert(1)',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseOver={(e) => e.target.style.filter = 'brightness(0) invert(1) drop-shadow(0 0 8px rgba(255, 255, 255, 0.8))'}
                    onMouseOut={(e) => e.target.style.filter = 'brightness(0) invert(1)'}
                  />
                </a>
              </Box>
            </Box>

            {/* Attribution */}
            <Box
              component="a"
              href="https://joshdlee.com"
              target="_blank"
              rel="noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                border: `1px solid ${cyberpunkColors.neonCyan}40`,
                background: 'rgba(0, 255, 255, 0.05)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  border: `1px solid ${cyberpunkColors.neonCyan}`,
                  boxShadow: `0 0 15px ${cyberpunkColors.neonCyan}40`,
                  background: 'rgba(0, 255, 255, 0.1)',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Rajdhani", sans-serif',
                  fontSize: '0.75rem',
                  color: cyberpunkColors.textSecondary,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                Built by
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Orbitron", sans-serif',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: cyberpunkColors.neonCyan,
                  textShadow: `0 0 10px ${cyberpunkColors.neonCyan}60`,
                  letterSpacing: '0.05em',
                }}
              >
                JOSH LEE
              </Typography>
            </Box>
          </Box>
        </footer>
      </ThemeProvider>
    </div>
  );
}

export default App;
